import { useState, useEffect } from 'react';
import { BookOpen, Lightbulb } from 'lucide-react';
import api from '../../services/api';
import Modal from '../ui/Modal';

interface Suggestion { id: string; title: string; category?: string | null }

/** Shows KB articles relevant to the ticket (by category/subject) near the reply box. */
export default function KbSuggestions({ query }: { query: string }) {
  const [items, setItems] = useState<Suggestion[]>([]);
  const [reading, setReading] = useState<{ title: string; body: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const q = (query || '').trim();
    if (!q) { setItems([]); return; }
    api.get('/kb/suggest', { params: { q } })
      .then(res => { if (!cancelled) setItems(res.data.articles ?? []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [query]);

  const openArticle = async (id: string) => {
    try {
      const res = await api.get(`/kb/${id}`);
      setReading({ title: res.data.article.title, body: res.data.article.body });
    } catch { /* ignore */ }
  };

  if (items.length === 0) return null;

  return (
    <div className="rounded-lg border border-amber-100 bg-amber-50/60 p-3">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 mb-1.5"><Lightbulb size={13} /> Suggested articles</p>
      <ul className="space-y-1">
        {items.map(a => (
          <li key={a.id}>
            <button onClick={() => openArticle(a.id)} className="inline-flex items-center gap-1.5 text-xs text-brand-dark hover:underline">
              <BookOpen size={12} /> {a.title}
            </button>
          </li>
        ))}
      </ul>
      {reading && (
        <Modal open onClose={() => setReading(null)} size="lg" title={reading.title}>
          <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{reading.body}</div>
        </Modal>
      )}
    </div>
  );
}
